import React from 'react';
import { Link } from 'react-router-dom';
import CrmLayout from '@/modules/crm/components/CrmLayout';
import { useCrmStats, useCrmContacts, useCrmOpportunities } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';

// Opportunity stage config for display
const OPP_STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: '🆕' },
  active: { label: 'Active', color: 'bg-orange-100 text-orange-700', icon: '💬' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700', icon: '✅' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-500', icon: '⏸️' },
};

export default function CrmDashboard() {
  const { toasts, hideToast } = useToast();
  const { data: stats, isLoading: loadingStats } = useCrmStats();
  const { data: recentContactsData, isLoading: loadingContacts } = useCrmContacts(1, 5);
  const { data: recentOppsData, isLoading: loadingOpps } = useCrmOpportunities(1, 5, 'open');

  const recentContacts = recentContactsData?.contacts || [];
  const recentOpportunities = recentOppsData?.opportunities || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getContactName = (contact) => {
    const parts = [contact.firstName, contact.lastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  const getContactInitial = (contact) => {
    return contact.firstName?.charAt(0)?.toUpperCase() || '?';
  };

  const getOppContactName = (opp) => {
    const parts = [opp.contactFirstName, opp.contactLastName].filter(Boolean);
    return parts.join(' ') || 'Unknown';
  };

  return (
    <CrmLayout toasts={toasts} onCloseToast={hideToast}>
      {/* Intro */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-800">
          <strong>Contacts</strong> are people you're tracking. <strong>Opportunities</strong> are potential deals linked to contacts — one contact can have multiple opportunities (e.g., different projects).
        </p>
      </div>

      {/* Opportunity Pipeline Stats */}
      <h2 className="text-lg font-semibold text-gray-700 mb-4">Opportunity Pipeline</h2>
      {loadingStats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-16 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-12 mb-1"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(OPP_STAGE_CONFIG).map(([stage, config]) => (
            <Link
              key={stage}
              to={`/crm/opportunities?stage=${stage}`}
              className={`bg-white rounded-xl border-2 p-4 hover:shadow-md transition-all ${
                stage === 'converted' ? 'border-green-200 hover:border-green-400' :
                stage === 'inactive' ? 'border-gray-200 hover:border-gray-300' :
                'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{config.icon}</span>
                <span className="text-sm font-medium text-gray-600">{config.label}</span>
              </div>
              <p className={`text-3xl font-bold ${
                stage === 'converted' ? 'text-green-600' :
                stage === 'inactive' ? 'text-gray-500' :
                'text-gray-900'
              }`}>
                {stats?.opportunities?.byStage?.[stage] || 0}
              </p>
            </Link>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl border-2 border-purple-200 p-6">
          <p className="text-sm font-medium text-gray-600 mb-1">Total Contacts</p>
          <p className="text-4xl font-bold text-purple-600">{stats?.contacts?.total || 0}</p>
          <p className="text-xs text-gray-500 mt-1">People tracked</p>
        </div>
        
        <div className="bg-white rounded-xl border-2 border-blue-200 p-6">
          <p className="text-sm font-medium text-gray-600 mb-1">Pipeline Value</p>
          <p className="text-4xl font-bold text-blue-600">{formatCurrency(stats?.pipeline?.estimatedValue || stats?.pipelineValue)}</p>
          <p className="text-xs text-gray-500 mt-1">Weighted by probability</p>
        </div>
        
        <div className="bg-white rounded-xl border-2 border-orange-200 p-6">
          <p className="text-sm font-medium text-gray-600 mb-1">Est. Monthly Recurring</p>
          <p className="text-4xl font-bold text-orange-600">{formatCurrency(stats?.pipeline?.estimatedMonthlyRecurring || 0)}</p>
          <p className="text-xs text-gray-500 mt-1">When deals close</p>
        </div>
        
        <div className="bg-white rounded-xl border-2 border-green-200 p-6">
          <p className="text-sm font-medium text-gray-600 mb-1">Converted Value</p>
          <p className="text-4xl font-bold text-green-600">{formatCurrency(stats?.pipeline?.convertedInitialValue || stats?.convertedValue)}</p>
          <p className="text-xs text-gray-500 mt-1">{stats?.opportunities?.converted || 0} deals converted</p>
        </div>
      </div>

      {/* Two columns: Recent Contacts and Recent Opportunities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Contacts */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Contacts</h2>
            <Link
              to="/crm/contacts"
              className="text-sm text-zapt-purple hover:text-zapt-purple/80 font-medium"
            >
              View all
            </Link>
          </div>

          {loadingContacts ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-3 animate-pulse">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentContacts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No contacts yet</p>
              <Link
                to="/crm/contacts/new"
                className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
              >
                Add your first contact
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentContacts.map((contact) => (
                <Link
                  key={contact.id}
                  to={`/crm/contacts/${contact.id}`}
                  className="flex items-center gap-4 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                >
                  <div className="w-10 h-10 bg-zapt-purple/10 rounded-full flex items-center justify-center">
                    <span className="text-zapt-purple font-medium">
                      {getContactInitial(contact)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{getContactName(contact)}</p>
                    <p className="text-sm text-gray-500 truncate">
                      {contact.company || contact.email || 'No details'}
                    </p>
                  </div>
                  {contact.opportunityCount > 0 && (
                    <span className="text-xs text-gray-500">
                      {contact.opportunityCount} opp{contact.opportunityCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Recent Opportunities */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Active Opportunities</h2>
            <Link
              to="/crm/opportunities"
              className="text-sm text-zapt-purple hover:text-zapt-purple/80 font-medium"
            >
              View all
            </Link>
          </div>

          {loadingOpps ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-3 animate-pulse">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentOpportunities.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No opportunities yet</p>
              <Link
                to="/crm/opportunities/new"
                className="inline-flex items-center px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90"
              >
                Add your first opportunity
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentOpportunities.map((opp) => {
                const stageConfig = OPP_STAGE_CONFIG[opp.stage] || OPP_STAGE_CONFIG.new;
                return (
                  <Link
                    key={opp.id}
                    to={`/crm/opportunities/${opp.id}`}
                    className="flex items-center gap-4 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{opp.title}</p>
                      <p className="text-sm text-gray-500 truncate">
                        {getOppContactName(opp)} {opp.contactCompany && `• ${opp.contactCompany}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${stageConfig.color}`}>
                        {stageConfig.label}
                      </span>
                      {(opp.estimatedInitialValue || opp.estimatedMonthlyValue) && (
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {formatCurrency((parseFloat(opp.estimatedInitialValue) || 0) + (parseFloat(opp.estimatedMonthlyValue) || 0) * 12)}
                        </p>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Link
          to="/crm/contacts/new"
          className="inline-flex items-center px-6 py-3 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 font-medium"
        >
          + Add New Contact
        </Link>
        <Link
          to="/crm/opportunities/new"
          className="inline-flex items-center px-6 py-3 border-2 border-zapt-purple text-zapt-purple rounded-lg hover:bg-zapt-purple/5 font-medium"
        >
          + Add New Opportunity
        </Link>
      </div>
    </CrmLayout>
  );
}
